<template>
  <Card v-if="isEdit" class="container">
    <div class="cardTitle">{{ $t('Предмет договора / НМЦ') }}</div>
    <TextArea
      label="Предмет договора"
      v-model="subjectValue"
      :rules="[].concat(validation.required(), validation.text(2000))"
      :disabled="disabled"
    />
    <div class="container-money">
      <MoneyInput
        v-if="!isWithoutStartMaxPrice"
        label="НМЦ"
        v-model="startMaxPriceValue"
        :rules="subjectInfoStore.isPositionsRequired ? [] : startMaxPriceRules"
        :disabled="isStartMaxPriceDisabled"
        :class="{ 'not-required': this.subjectInfoStore.isPositionsRequired }"
      />
      <SelectInput
        v-if="!isWithoutStartMaxPrice && !subjectInfoStore.isPositionsRequired"
        label="НДС"
        class="nds-input"
        v-model="isNdsValue"
        :items="itemsNds"
        :rules="[
          value => (value !== null) || $t('Это поле обязательно для заполнения')
        ]"
      />
    </div>
    <v-checkbox
      v-if="!subjectInfoStore.hideStartMaxPriceCheckbox"
      :label="$t('без указания НМЦ')"
      class="not-required"
      v-model="isWithoutStartMaxPriceValue"
      :disabled="disabled"
      hide-details
    />
    <TextArea
      label="Дополнительная информация"
      class="not-required"
      v-model="additionalInformationValue"
      :rules="validation.text(2000)"
      :disabled="disabled"
    />
  </Card>
  <v-expansion-panels v-else v-model="panel" class="mb-5">
    <v-expansion-panel
      :title="$t(isApplication ? 'Предмет договора' : 'Предмет договора / НМЦ')"
      class="cardTitle"
    >
      <v-expansion-panel-text>
        <v-textarea
          :label="$t('Предмет договора')"
          :model-value="subjectValue"
          variant="underlined"
          class="not-required"
          rows="1" auto-grow max-rows="4"
          readonly
          no-resize
        />
        <div class="container-money">
          <v-text-field
            v-if="!isApplication"
            :label="$t('НМЦ')"
            :model-value="startMaxPriceValue === null ? $t('Без объявления цены') : maxStartPricePrepared"
            variant="underlined"
            class="not-required"
            readonly
          />
          <v-text-field
            v-if="!isApplication && !isWithoutStartMaxPrice && startMaxPrice !== null && !subjectInfoStore.isPositionsRequired"
            :label="$t('НДС')"
            :model-value="isNdsValue ? $t('c НДС') : $t('без НДС')"
            variant="underlined"
            class="nds-input not-required"
            readonly
          />
        </div>
        <v-textarea
          :label="$t('Дополнительная информация')"
          :model-value="additionalInformationValue"
          variant="underlined"
          class="not-required"
          rows="1" auto-grow max-rows="4"
          readonly
          no-resize
        />
        <v-btn
          v-if="procedureStepCode === procedureStep.COMPLETED &&
            procedure.mode === 'ENTIRE' &&
            result.isCurrentContragentWinner
          "
          class="h-auto pt-1 pb-1"
          color="primary"
          density="comfortable"
          variant="outlined"
          @click="downloadResults()"
        >
          <v-icon class="mr-1">custom:downloadFileIcon</v-icon>{{ $t('Скачать результаты') }}</v-btn>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import TextArea from "@intec/vue-components/form/TextArea.vue";
import SelectInput from "@intec/vue-components/form/SelectInput.vue";
import MoneyInput from '@/components/form/MoneyInput.vue';
import validation from '@/components/validation';
import procedureStep from "@/components/ProcedureStep";
import { useSubjectInfoStore } from "@/stores/subjectInfoStore";
import { useLibraryStore } from "@/stores/library";
import { onMounted, watch } from "vue";

export default {
  name: 'SubjectInfo',
  components: {MoneyInput, TextArea, SelectInput, Card},
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    isApplication: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    procedureType: {
      type: String
    },
    subject: {
      type: String
    },
    additionalInformation: {
      type: String
    },
    startMaxPrice: {},
    isWithoutStartMaxPrice: {
      type: Boolean
    },
    isNds: {
      type: Boolean
    },
    startMaxPriceRules: {},
    procedureStepCode: {
      type: String
    },
    result: {
      type: Object
    },
    procedure: {
      type: Object
    }
  },
  emits: [
    'update:subject',
    'update:startMaxPrice',
    'update:isWithoutStartMaxPrice',
    'update:isNds',
    'update:additionalInformation'
  ],
  data() {
    return {
      itemsNds: [
        { title: this.$t('c НДС'), value: true },
        { title: this.$t('без НДС'), value: false }
      ],
      rows: 8,
      validation,
      procedureStep,
      panel: !this.isApplication ? [0] : undefined
    };
  },
  setup(props) {
    const subjectInfoStore = useSubjectInfoStore();
    const libraryStore = useLibraryStore();

    onMounted(() => {
      libraryStore.callActionByCode('initSubjectInfoOptions', {
        procedureType: props.procedureType
      });
    });

    watch(
      () => props.procedureType,
      (newType) => {
        subjectInfoStore.setDefaults();
        libraryStore.callActionByCode('initSubjectInfoOptions', {
          procedure: props.procedure,
          procedureType: newType
        });
      }
    );

    return {
      subjectInfoStore,
      libraryStore
    };
  },
  computed: {
    isStartMaxPriceDisabled() {
      return this.disabled || this.isWithoutStartMaxPrice || this.subjectInfoStore.isPositionsRequired;
    },
    subjectValue: {
      get() {
        return this.subject
      },
      set(value) {
        this.$emit('update:subject', value)
      }
    },
    startMaxPriceValue: {
      get() {
        // Если позиции обязательны - берем сумму всех позиций.
        if (this.isEdit && this.subjectInfoStore.isPositionsRequired) {
          if (!this.procedure.positions) return null;
          return this.procedure.positions.reduce(
            (sum, pos) => sum + ((Number(pos.startMaxPrice) || 0) * Number(pos?.quantity)),
            0
          ) || null;
        }

        return this.startMaxPrice === null ? null : Number(this.startMaxPrice);
      },
      set(value) {
        this.$emit('update:startMaxPrice', value);
      }
    },
    isWithoutStartMaxPriceValue: {
      get() {
        return this.isWithoutStartMaxPrice;
      },
      set(value) {
        this.startMaxPriceValue = null;
        this.isNdsValue = null;
        this.$emit('update:isWithoutStartMaxPrice', value);
      }
    },
    isNdsValue: {
      get() {
        return this.isNds;
      },
      set(value) {
        this.$emit('update:isNds', value);
      }
    },
    additionalInformationValue: {
      get() {
        return this.additionalInformation;
      },
      set(value) {
        this.$emit('update:additionalInformation', value);
      }
    },
    maxStartPricePrepared() {
      return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'USD' })
        .format(this.startMaxPrice)
    }
  },

  methods: {
    async downloadResults() {
      // Пытаемся получить ссылку от внешнего модуля.
      const overrideLink = this.libraryStore.callFunctionByCode('getLinkDownloadResults', {
        procedure: this.procedure
      }).linkDownloadResults ?? null;

      // Если модуль вернул строку — используем её, иначе дефолтную.
      window.location.href = typeof overrideLink === 'string'
        ? overrideLink
        : `/procedures/${this.procedure.id}/resultsExport`;
    }
  }
};
</script>
<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 15px;
}

.container-money {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

:deep(.v-messages__message) {
  font-size: 12px;
  font-weight: 500;
}

:deep(.v-input__details) {
  padding-top: 6px;
}

.nds-input {
  max-width: 160px;
}

:deep(.v-select > .v-input__control) {
  height: 58.5px;
}

:deep(.v-label::after) {
  content: ' *';
  color: rgba(var(--error-color));
}

:deep(.not-required .v-label::after) {
  content: '';
  color: transparent;
}

:deep(.v-expansion-panel-title) {
  font-weight: 500;
  font-size: 18px;
}

:deep(.v-expansion-panel-title--active) {
  background-color: #F5F5F5;
}

:deep(.v-expansion-panel-text) {
  font-size: 16px;
  font-weight: 400;
  padding-left: 10px;
  word-wrap: break-word;
  white-space: normal
}

:deep(.v-field--focused.v-field--variant-underlined .v-field__outline::after) {
  transform: scaleX(0);
}

:deep(.v-field.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 0.38;
}

@media (hover: hover) {
  :deep(.v-field:hover .v-field__outline) {
    --v-field-border-opacity: 0.38;
  }
}
</style>
